/**
 *  @swagger
 *  definitions:
 *    RegattaTrackEventParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Public:
 *          description: Public of the Regatta
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaTrackEvent
 *          type: string
 *        StartDate:
 *          description: Start Date of the Regatta
 *          type: string
 *          format: date-time
 *        EndDate:
 *          description: End Date of the Regatta
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegattaTrackEventCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaTrackEventParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Public
 *            - Name
 *            - StartDate
 *            - EndDate
 *
 *  @swagger
 *  definitions:
 *    RegattaTrackEventQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaTrackEvent
 *              type: string
 *        - $ref: '#/definitions/RegattaTrackEventParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaTrackEventUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaTrackEventQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaTrackEvent:
 *      allOf:
 *        - $ref: '#/definitions/RegattaTrackEventCreateParams'
 *        - $ref: '#/definitions/RegattaTrackEventUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaTrackEvent:
 *        allOf:
 *          - $ref: '#/definitions/RegattaTrackEvent'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaTrackEvent
 *                $ref: '#/components/schemas/Regatta'
 *              RegattaCourses:
 *                description: RegattaCourses of the RegattaTrackEvent
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaCourse'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  EndDate: Date;
  Name: string;
  Public: boolean;
  StartDate: Date;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  EndDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  StartDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaTrackEvent'> => {
  const RegattaTrackEvent = stack.define(
    'RegattaTrackEvent',
    fields(Sequelize),
    {
      tableName: 'RegattaTrackEvents',
    },
  );

  RegattaTrackEvent.associate = (models: $ModelClasses) => {
    models.RegattaTrackEvent.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaTrackEvent.belongsToMany(
      models.RegattaCourse,
      {
        as: 'RegattaCourses',
        foreignKey: 'RegattaTrackEventId',
        otherKey: 'RegattaCourseId',
        through: models.RegattaTrackEventRegattaCourse,
      },
    );
  };

  RegattaTrackEvent.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaTrackEvent;
});
